import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverviewCardComponent } from './overview-card/overview-card.component';
import { CampaignOverviewResponse } from '../../../components/my-campaigns-screen/types';
import { BrandOverviewResponse } from '../../../components/my-brand-screen/types';
import { addDollars, addPercent, addSeconds, ordinal, reduceNumber, round } from '../../../utils/numbers';
import { ImpressionsRangePipe } from '../../../pipes/impressions-range.pipe';
import { ShortNumberPipe } from '../../../pipes/short-number.pipe';
import { MyCompetitionOverviewData, MyCompetitionOverviewMetrics, MyCompetitionOverviewResponse, SpendEfficiencyOverviewResponse } from '../../../components/my-competition-screen/types';
import {
  CardType, MetricIcon, MetricTitle, MetricType, OverviewCardConfig, OverviewResponse,
  SHARE_OF_VOICE_RESPONSE, SingleMetricConfig, Tooltip, Trend, ValueType
} from './types';
import { ShortDisplayValuePipe } from '../../../pipes/short-display-value.pipe';
import { AiPresenceOverviewResponse } from '../../../components/ai-presence-screen/types';
import { SocialOverviewBrandData, SocialOverviewResponse } from '../../../components/social-overview-screen-a/types';
import {tooltips} from '../../../utils/tooltips';
import {AskPolarisButtonComponent} from '../../../components/copilot/components/ask-polaris/ask-polaris-button/ask-polaris-button.component';
import {WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN} from '../../../components/copilot/types';

@Component({
  selector: 'app-overview-tiles',
  standalone: true,
  imports: [CommonModule, OverviewCardComponent, AskPolarisButtonComponent],
  providers: [ImpressionsRangePipe, ShortNumberPipe, ShortDisplayValuePipe],
  templateUrl: './overview-tiles.component.html',
  styleUrl: './overview-tiles.component.scss'
})
export class OverviewTilesComponent {

  protected cards: OverviewCardConfig[] = [];
  @Input() brandColors: Record<string, string> = {};
  @Input() myBrand?: string;
  @Input() direction: 'horizontal' | 'vertical' = 'horizontal';
  @Input() tileWidth?: number;
  @Input() showTitle = true;
  @Input() type?: 'campaign' | 'brand' | 'competition' | 'ai-presence' | 'spend-efficiency' | 'social-overview';
  @Input() loading = false;
  @Input() loadings?: boolean[];
  @Input() showAskPolaris = false;
  @Input() tooltips: Record<string, string> = {};
  @Input()
  set overview(value: OverviewResponse | undefined) {
    this.cards = this.buildCards(value);
  }

  constructor(
    private impressionsRangePipe: ImpressionsRangePipe,
    private shortDisplayValuePipe: ShortDisplayValuePipe
  ) { }

  ngOnInit(): void {
    if (!this.type) {
      throw new Error("You must provide type");
    }
  }

  private buildCards(overview?: OverviewResponse): OverviewCardConfig[] {
    if (this.type === 'brand') {
      return this.getBrandCards(overview as BrandOverviewResponse);
    } else if (this.type === 'campaign') {
      return this.getCampaignCards(overview as CampaignOverviewResponse);
    } else if (this.type === 'competition') {
      return this.getCompetitionCards(overview as MyCompetitionOverviewResponse);
    } else if (this.type === 'ai-presence') {
      return this.getAiPresenceCards(overview as AiPresenceOverviewResponse);
    } else if (this.type === 'spend-efficiency') {
      return this.getSpendEfficiencyCards(overview as SpendEfficiencyOverviewResponse);
    } else if (this.type === 'social-overview') {
      return this.getSocialOverviewCards(overview as SocialOverviewResponse);
    }
    return [];
  }

  private getTrend(value: number, expectingDecrease: boolean = false): Trend {
    return {
      value: Math.abs(value),
      arrowDirection: value > 0 ? 'up' : 'down',
      status: this.getStatus(value, expectingDecrease)
    };
  }

  private getStatus(value: number, expectingDecrease: boolean): ValueType {
    switch (true) {
      case value === 0:
        return ValueType.NEUTRAL;
      case expectingDecrease:
        return value < 0 ? ValueType.POSITIVE : ValueType.NEGATIVE;
      default:
        return value > 0 ? ValueType.POSITIVE : ValueType.NEGATIVE;
    }
  }

  public getAskPolarisWidgetData() {
    switch (this.type) {
      case 'social-overview':
        return WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN.SOCIAL_OVERVIEW;
      case 'brand':
        return WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN.MY_BRAND_OVERVIEW;
      default:
        return WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN.MY_COMPETITION_OVERVIEW;
    }
  }
  private getBrandCards(overview: BrandOverviewResponse): OverviewCardConfig[] {
    return [
      {
        type: CardType.SINGLE,
        title: MetricTitle.ATTENTION,
        iconPath: MetricIcon.ATTENTION,
        value: this.getFormatedMetric(MetricType.ATTENTION, overview?.attention),
        trend: this.getTrend(overview?.attentionTrend)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.DWELL_TIME,
        iconPath: MetricIcon.DWELL_TIME,
        value: this.getFormatedMetric(MetricType.DWELL_TIME, overview?.dwellTime),
        trend: this.getTrend(overview?.dwellTimeTrend)
      },
      this.getBrandCtrCard(overview),
      {
        type: CardType.SINGLE,
        title: MetricTitle.CLICK_RISK,
        iconPath: MetricIcon.CLICK_RISK,
        value: this.getFormatedMetric(MetricType.CLICK_RISK, overview?.clickRisk)
      },
      {
        type: CardType.MULTIPLE,
        metrics: [
          {
            title: MetricTitle.IMPRESSIONS,
            value: this.impressionsRangePipe.transform(overview?.minImpressions, overview?.maxImpressions) || undefined
          },
          {
            title: MetricTitle.VIEWABILITY,
            value: this.getFormatedMetric(MetricType.VIEWABILITY, overview?.viewability)
          },
          {
            title: MetricTitle.AVG_CPM,
            value: this.getFormatedMetric(MetricType.CPM, overview?.cpm)
          },
        ]
      },
    ]
  }

  private getCampaignCards(overview: CampaignOverviewResponse): OverviewCardConfig[] {
    return [
      {
        type: CardType.SINGLE,
        title: MetricTitle.ATTENTION,
        iconPath: MetricIcon.ATTENTION,
        value: this.getFormatedMetric(MetricType.ATTENTION, overview?.attention),
        trend: this.getTrend(overview?.attentionTrend)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.DWELL_TIME,
        iconPath: MetricIcon.DWELL_TIME,
        value: this.getFormatedMetric(MetricType.DWELL_TIME, overview?.timeInView),
        trend: this.getTrend(overview?.timeInViewTrend)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.CLUTTER,
        iconPath: MetricIcon.CLUTTER,
        value: this.getFormatedMetric(MetricType.CLUTTER, overview?.clutter),
        trend: this.getTrend(overview?.clutterTrend, true)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.CLICK_RISK,
        iconPath: MetricIcon.CLICK_RISK,
        value: this.getFormatedMetric(MetricType.CLICK_RISK, overview?.clickRisk)
      },
      {
        type: CardType.MULTIPLE,
        metrics: [
          {
            title: MetricTitle.IMPRESSIONS,
            value: this.getFormatedMetric(MetricType.IMPRESSIONS, overview?.impressions)
          },
          {
            title: MetricTitle.CTR,
            value: this.getFormatedMetric(MetricType.CTR, overview?.ctr)
          },
          {
            title: MetricTitle.VIEWABILITY,
            value: this.getFormatedMetric(MetricType.VIEWABILITY, overview?.viewability)
          },
        ]
      },
    ]
  }

  private getCompetitionCards(overview: MyCompetitionOverviewResponse): OverviewCardConfig[] {
    const sortedBrands = this.sortBrandsByMyBrand(overview?.brands);
    return [
      {
        type: CardType.SINGLE,
        title: MetricTitle.ATTENTION,
        iconPath: MetricIcon.ATTENTION,
        value: this.getCompetitionDiffValue(overview?.diff?.attention),
        valueType: this.getValueType(overview?.diff?.attention),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.attention),
        tooltip: this.getTooltip(sortedBrands, MetricType.ATTENTION)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.DWELL_TIME,
        iconPath: MetricIcon.DWELL_TIME,
        value: this.getCompetitionDiffValue(overview?.diff?.dwellTime),
        valueType: this.getValueType(overview?.diff?.dwellTime),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.dwellTime),
        tooltip: this.getTooltip(sortedBrands, MetricType.DWELL_TIME)
      },
      this.getCompetitionCtrCard(overview, sortedBrands),
      {
        type: CardType.MULTIPLE,
        metrics: [
          {
            title: MetricTitle.IMPRESSIONS,
            value: (typeof overview?.diff?.impressions === 'number') ? `${addPercent((overview?.diff?.impressions))} of total` : undefined,
            tooltip: this.getTooltip(sortedBrands, MetricType.IMPRESSIONS)
          },
          {
            title: MetricTitle.VIEWABILITY,
            value: this.getCompetitionDiffValue(overview?.diff?.viewability),
            valueType: this.getValueType(overview?.diff?.viewability),
            tooltip: this.getTooltip(sortedBrands, MetricType.VIEWABILITY)
          },
          {
            title: MetricTitle.AVG_CPM,
            value: this.getCompetitionDiffValue(overview?.diff?.cpm),
            valueType: this.getValueType(overview?.diff?.cpm, true),
            tooltip: this.getTooltip(sortedBrands, MetricType.CPM)
          },
        ]
      },
    ]
  }

  private getAiPresenceCards(overview: AiPresenceOverviewResponse): OverviewCardConfig[] {
    return [
      {
        type: CardType.SINGLE,
        title: MetricTitle.BRAND_VISIBILITY,
        value: this.getFormatedMetric(MetricType.BRAND_VISIBILITY, overview?.metrics?.brandVisibility),
        trend: this.getTrend(overview?.trend?.brandVisibility),
        titleTooltip: this.tooltips['brandVisibility']
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.AI_SENTIMENT,
        value: this.getFormatedMetric(MetricType.AI_SENTIMENT, overview?.metrics?.sentiment),
        trend: this.getTrend(overview?.trend?.sentiment),
        titleTooltip: this.tooltips['sentiment']
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.MY_SHARE_OF_VOICE,
        value: this.getFormatedMetric(MetricType.MY_SHARE_OF_VOICE, overview?.metrics?.myShareOfVoice),
        trend: this.getTrend(overview?.trend?.myShareOfVoice),
        titleTooltip: this.tooltips['myShareOfVoice']
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.AI_VISIBILITY_RETURN,
        value: this.getFormatedMetric(MetricType.AI_VISIBILITY_RETURN, overview?.metrics?.visibilityReturn),
        trend: this.getTrend(overview?.trend?.visibilityReturn),
        titleTooltip: this.tooltips['visibilityReturn']
      }
    ]
  }

  private getSpendEfficiencyCards(overview: SpendEfficiencyOverviewResponse): OverviewCardConfig[] {
    const formattedBrands: MyCompetitionOverviewData[] = overview?.brands?.map(brand => ({
      key: brand.brandKey,
      name: brand.brandName,
      attention: brand.attention,
      efficiency: brand.efficiency,
      impact: brand.impact,
      impressions: brand.impressions
    } as any)) || [];
    const sortedBrands = this.sortBrandsByMyBrand(formattedBrands);

    return [
      {
        type: CardType.SINGLE,
        title: 'Overall Efficiency by CPM',
        value: this.getCompetitionDiffValue(overview?.efficiencyDiff !== undefined ? Math.round(overview.efficiencyDiff) : undefined),
        valueType: this.getValueType(overview?.efficiencyDiff),
        valueDescription: this.getCompetitionValueDescription(overview?.efficiencyDiff),
        //tooltip: this.getTooltip(sortedBrands, MetricType.EFFICIENCY),
        titleTooltip: tooltips.myCompetition.spendEfficiencyOverview.overallEfficiency,
        titleTooltipPlace: 'above'
      },
      {
        type: CardType.SINGLE,
        title: 'Impact by Volume',
        value: this.getCompetitionDiffValue(overview?.impactDiff !== undefined ? Math.round(overview.impactDiff) : undefined),
        valueType: this.getValueType(overview?.impactDiff),
        valueDescription: this.getCompetitionValueDescription(overview?.impactDiff),
        //tooltip: this.getTooltip(sortedBrands, MetricType.IMPACT),
        titleTooltip: tooltips.myCompetition.spendEfficiencyOverview.impactByVolume,
        titleTooltipPlace: 'above'
      },
      {
        type: CardType.SINGLE,
        title: 'TruAttention™ Rank',
        value: overview?.attentionRank !== undefined ? ordinal(overview.attentionRank) : undefined,
        valueType: overview?.attentionRank === 1 ? ValueType.POSITIVE : (overview?.attentionRank !== undefined && overview.attentionRank < 4) ? ValueType.MID_POSITION : ValueType.NEGATIVE,
        //tooltip: this.getTooltip(sortedBrands, MetricType.ATTENTION),
        titleTooltip: tooltips.myCompetition.spendEfficiencyOverview.attentionRank,
        titleTooltipPlace: 'above'
      }
    ]
  }

  private getFormatedMetric(metric: MetricType, value?: string | number): string | undefined {
    switch (metric) {
      case MetricType.BRAND_VISIBILITY:
      case MetricType.AI_SENTIMENT:
      case MetricType.MY_SHARE_OF_VOICE:
      case MetricType.ATTENTION:
        return (typeof value === 'number') ? addPercent(round(value, 0)) : undefined;
      case MetricType.DWELL_TIME:
        return (typeof value === 'number') ? addSeconds(round(value, 0)) : undefined;
      case MetricType.AI_VISIBILITY_RETURN:
      case MetricType.CLUTTER:
        return (typeof value === 'number') ? round(value, 2).toString() : undefined;
      case MetricType.IMPRESSIONS:
        return (typeof value === 'number') ? reduceNumber(value) : undefined;
      case MetricType.VIEWABILITY:
        return (typeof value === 'number') ? addPercent(value.toFixed(0)) : undefined;
      case MetricType.CPM:
        return (typeof value === 'number') ? addDollars(value.toFixed(2)) : undefined;
      case MetricType.CTR:
        return (typeof value === 'number') ? addPercent(round(value, 2)) : undefined;
      case MetricType.CLICK_RISK:
        return value?.toString();
      case MetricType.EFFICIENCY:
      case MetricType.IMPACT:
        return (typeof value === 'number') ? reduceNumber(value) : value?.toString();
      case MetricType.SPEND:
        return (typeof value === 'number') ? `$${reduceNumber(value)}` : undefined;
      default:
        return value?.toString();
    }
  }

  private getValueType(value?: number, expectingDecrease: boolean = false): ValueType | undefined {
    if (typeof value !== 'number') {
      return undefined;
    } else if (value === 0) {
      return ValueType.NEUTRAL;
    } else if (expectingDecrease) {
      return value < 0 ? ValueType.POSITIVE : ValueType.NEGATIVE;
    } else {
      return value > 0 ? ValueType.POSITIVE : ValueType.NEGATIVE;
    }
  }

  private getCompetitionDiffValue(value?: number, hasData = true, isShareOfVoice = false): string | undefined {
    if (typeof value !== 'number' || !hasData) {
      return undefined;
    } else if (value === 0) {
      return '~';
    } else if (value > 0 && !isShareOfVoice) {
      return `+${addPercent(value)}`;
    } else {
      return addPercent(value);
    }
  }

  private getCompetitionValueDescription(value?: number): string | undefined {
    if (typeof value !== 'number') {
      return undefined;
    } else if (value === 0) {
      return 'Same as competitors';
    } else {
      return 'vs competitors';
    }
  }

  private getTooltip(brands: MyCompetitionOverviewData[], metric: MetricType): Tooltip[] {
    const isImpressions = (metric === MetricType.IMPRESSIONS);
    return brands?.map((brand: MyCompetitionOverviewData) => {
      // a brand with no data in the range comes back with null metrics: bold N/A, no impressions in parentheses
      const impressions = brand.impressions === null ? undefined : this.shortDisplayValuePipe.transform(brand.impressions, true);
      const value = isImpressions ? impressions : this.getFormatedMetric(metric, brand[metric as keyof MyCompetitionOverviewMetrics] ?? undefined);
      return {
        brand: brand.name,
        impressions: isImpressions ? undefined : impressions,
        value: value ?? ValueType.NA,
        color: this.brandColors[brand.key]
      };
    });
  }

  private getBrandCtrCard(overview: BrandOverviewResponse): SingleMetricConfig {
    return {
      type: CardType.SINGLE,
      title: MetricTitle.CTR,
      iconPath: MetricIcon.CTR,
      value: this.getFormatedMetric(MetricType.CTR, overview?.ctr),
      trend: this.getTrend((overview?.ctrTrend))
    };
  }

  private getCompetitionCtrCard(overview: MyCompetitionOverviewResponse, sortedBrands: MyCompetitionOverviewData[]): SingleMetricConfig {
    const isLegacy = overview?.diff?.ctr == null;
    const diffValue = overview?.diff?.ctr
    return {
      type: CardType.SINGLE,
      title: MetricTitle.CTR,
      iconPath: MetricIcon.CTR,
      value: this.getCompetitionDiffValue(diffValue),
      valueType: this.getValueType(diffValue, isLegacy),
      valueDescription: this.getCompetitionValueDescription(diffValue),
      tooltip: this.getTooltip(sortedBrands, MetricType.CTR)
    };
  }

  private sortBrandsByMyBrand<T extends { key: string; name: string }>(brands: T[]): T[] {
    if (!brands) return [];
    const myBrand = brands.find(brand => brand.key === this.myBrand);
    const otherBrands = brands.filter(brand => brand.key !== this.myBrand);
    return myBrand ? [myBrand, ...otherBrands] : otherBrands;
  }

  private getSocialOverviewCards(overview: SocialOverviewResponse): OverviewCardConfig[] {
    const sortedBrands = this.sortBrandsByMyBrand(overview?.brands);
    return [
      {
        type: CardType.SINGLE,
        title: MetricTitle.MY_SHARE_OF_VOICE,
        value: this.getSocialDiffValue(overview?.diff?.shareOfVoice, true),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.shareOfVoice),
        tooltip: this.getSocialTooltip(sortedBrands, SHARE_OF_VOICE_RESPONSE)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.SPEND,
        value: this.getSocialDiffValue(overview?.diff?.spend),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.spend),
        tooltip: this.getSocialTooltip(sortedBrands, MetricType.SPEND)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.IMPRESSIONS,
        value: this.getSocialDiffValue(overview?.diff?.impressions),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.impressions),
        tooltip: this.getSocialTooltip(sortedBrands, MetricType.IMPRESSIONS)
      },
      {
        type: CardType.SINGLE,
        title: MetricTitle.CPM,
        value: this.getSocialDiffValue(overview?.diff?.cpm),
        valueDescription: this.getCompetitionValueDescription(overview?.diff?.cpm),
        tooltip: this.getSocialTooltip(sortedBrands, MetricType.CPM)
      }
    ];
  }

  private getSocialDiffValue(value?: number, isShareOfVoice = false): string | undefined {
    if (typeof value !== 'number') return undefined;
    if (value === 0) return '~';
    const formatted = addPercent(round(value, 2));
    return (value > 0 && !isShareOfVoice) ? `+${formatted}` : formatted;
  }

  private getSocialTooltip(brands: SocialOverviewBrandData[], metric: typeof SHARE_OF_VOICE_RESPONSE | MetricType.SPEND | MetricType.IMPRESSIONS | MetricType.CPM): Tooltip[] {
    const isImpressions = metric === MetricType.IMPRESSIONS;
    return brands?.map(brand => ({
      brand: brand.name,
      impressions: isImpressions ? undefined : (brand.impressions ? this.shortDisplayValuePipe.transform(brand.impressions, true) : '0'),
      value: this.getSocialTooltipValue(brand, metric) ?? 'N/A',
      color: this.brandColors[brand.key]
    })) ?? [];
  }

  private getSocialTooltipValue(brand: SocialOverviewBrandData, metric: typeof SHARE_OF_VOICE_RESPONSE | MetricType.SPEND | MetricType.IMPRESSIONS | MetricType.CPM): string | undefined {
    switch (metric) {
      case SHARE_OF_VOICE_RESPONSE:
        return brand.shareOfVoice !== null ? this.getFormatedMetric(MetricType.MY_SHARE_OF_VOICE, brand.shareOfVoice) : undefined;
      case MetricType.IMPRESSIONS:
        return brand.impressions !== null && brand.impressions !== undefined ? this.shortDisplayValuePipe.transform(brand.impressions, true) : undefined;
      case MetricType.SPEND:
        return brand.spend !== null ? this.getFormatedMetric(MetricType.SPEND, brand.spend) : undefined;
      case MetricType.CPM:
        return brand.cpm !== null ? this.getFormatedMetric(MetricType.CPM, brand.cpm) : undefined;
    }
  }

  protected getCardLoadingState(index: number): boolean {
    return this.loadings ? (this.loadings[index] ?? this.loading) : this.loading;
  }

  protected getCardWidth(): string {
    if (this.type === 'spend-efficiency') {
      return '100%';
    }
    if (this.type === 'social-overview') {
      return 'fill';
    }
    return this.type === 'competition' || this.type === 'ai-presence' ? '243px' : '183px';
  }

  protected readonly WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN = WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN;
}
